/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import escape from 'lodash/escape';

import { ConnectorError } from './ConnectorError';
import { structuredDocumentExtractionProcess, unstructuredDocumentExtractionProcess } from './bpmn-snippets';
import runInstanceWithResult from './run-instance-with-result';

/**
 * @typedef {import('./types').ExtractionResult} ExtractionResult
 * @typedef {import('./types').UnstructuredExtractionTaxonomy} UnstructuredExtractionTaxonomy
 * @typedef {import('services/IdpProjectService').IdpDocument} IdpDocument
 */

export const CloudProvider = {
  AWS: 'aws',
  VERTEX: 'vertex'
};
Object.freeze(CloudProvider);

const extractionBaseRequest = {
  type: CloudProvider.AWS,
  authentication: {
    type: 'credentials',
    accessKey: '{{secrets.IDP_AWS_ACCESSKEY}}',
    secretKey: '{{secrets.IDP_AWS_SECRETKEY}}'
  },
  configuration: {
    region: '{{secrets.IDP_AWS_REGION}}'
  },
  s3BucketName: '{{secrets.IDP_AWS_BUCKET_NAME}}',
  extractionEngineType: 'AWS_TEXTRACT'
};

/**
 * Runs an **unstructured**‑document extraction workflow in Camunda
 * and returns the sanitized extraction result for every successfully
 * completed process instance.
 *
 * @async
 * @function extractUnstructuredDocument
 * @param {Object} params – Wrapper object for all arguments.
 * @param {string} params.idpProjectId – IDP project ID.
 * @param {string} params.clusterId – Cluster ID associated with the IDP project.
 * @param {Array<IdpDocument>} params.idpDocuments – One or more documents to process.
 * @param {UnstructuredExtractionTaxonomy} params.taxonomy – Taxonomy JSON that describes the fields to extract.
 * @param {string} params.modelId – Identifier of the LLM model to be used by the connector.
 * @param {Object} [params.baseRequest=extractionBaseRequest]  Overrides the default AWS‑Textract request.
 * @throws {ConnectorError} – If the workflow signals an application‑level failure.
 * @returns {Promise<Array<ExtractionResult>>} – One result per input document.
 */
export async function extractUnstructuredDocument({
  idpProjectId,
  clusterId,
  idpDocuments,
  taxonomy,
  modelId,
  baseRequest = extractionBaseRequest
}) {
  const xml = unstructuredDocumentExtractionProcess
    .replace('{{TAXONOMY}}', escape(JSON.stringify(taxonomy)))
    .replace('{{CONVERSE_DATA}}', escape(JSON.stringify(getConverseData(modelId))))
    .replace('{{BASE_REQUEST}}', escape(JSON.stringify(baseRequest)));
  const instancePayloads = idpDocuments.map((document) => ({
    document: {
      'camunda.document.type': document.clusterDocumentType,
      storeId: document.clusterDocumentStoreId,
      documentId: document.clusterDocumentId,
      contentHash: document.clusterDocumentContentHash,
      metadata: {
        size: document.size
      }
    }
  }));
  return await performExtraction(idpProjectId, clusterId, xml, instancePayloads);
}

/**
 * Runs an **structured**‑document extraction workflow in Camunda
 * and returns the sanitized extraction result for every successfully
 * completed process instance.
 *
 * @async
 * @function extractStructuredDocument
 * @param {Object} params – Wrapper object for all arguments.
 * @param {string} params.idpProjectId – IDP project ID.
 * @param {string} params.clusterId – Cluster ID associated with the IDP project.
 * @param {Array<IdpDocument>} params.idpDocuments – Structured documents to be extracted.
 * @param {string} params.delimiter – Replacement string to be used to replace the space character.
 * @param {Array<string>} [params.includedFields=[]] - List of the key that will be included in the extracted variables.
 * @param {Object<string,string>} params.renameMappings – List of key value pairs to which extracted variables
 * will be renamed. Key represents text in document and value to which it will be replaced.
 * For ex. Key is 'Invoice ID', value is 'invoice'.
 * @param {Object} [params.baseRequest=extractionBaseRequest]  Overrides the default AWS‑Textract request.
 * @throws {ConnectorError} – If the workflow signals an application‑level failure.
 * @returns {Promise<Array<ExtractionResult>>} – One result per input document.
 */
export async function extractStructuredDocument({
  idpProjectId,
  clusterId,
  idpDocuments,
  delimiter,
  includedFields = [],
  renameMappings,
  baseRequest = extractionBaseRequest
}) {
  const xml = structuredDocumentExtractionProcess.replace('{{BASE_REQUEST}}', escape(JSON.stringify(baseRequest)));
  const instancePayloads = idpDocuments.map((idpDocument) => ({
    delimiter,
    includedFields,
    renameMappings,
    document: {
      'camunda.document.type': idpDocument.clusterDocumentType,
      storeId: idpDocument.clusterDocumentStoreId,
      documentId: idpDocument.clusterDocumentId,
      contentHash: idpDocument.clusterDocumentContentHash,
      metadata: {
        size: idpDocument.size
      }
    }
  }));
  return await performExtraction(idpProjectId, clusterId, xml, instancePayloads);
}

async function performExtraction(idpProjectId, clusterId, bpmnXml, instancePayloads) {
  const results = await runInstanceWithResult(idpProjectId, clusterId, bpmnXml, 600, instancePayloads);

  const fulfilledResults = results.filter((result) => result.status === 'fulfilled');
  const error = fulfilledResults.find((result) => !!result?.value?.variables?.error)?.value?.variables?.error;

  if (error) {
    throw new ConnectorError(error.message, error.type);
  }

  return fulfilledResults.map((result) => ({
    processInstanceKey: result.value.processInstanceKey,
    clusterDocumentId: result.value.variables.document.documentId,
    variables: sanitizeExtractedFields(result.value.variables.result.extractedFields),
    confidenceScore: result.value.variables.result.confidenceScore,
    originalKeys: result.value.variables.result.originalKeys
  }));
}

function getConverseData(modelId) {
  // TODO: Replace with model specific converse data when available.
  return {
    modelId: modelId,
    maxTokens: 512,
    temperature: 0.5,
    topP: '0.9'
  };
}

function sanitizeExtractedFields(extractedFields) {
  if (!extractedFields || typeof extractedFields !== 'object') {
    return extractedFields;
  }

  const sanitized = { ...extractedFields };

  for (const key in sanitized) {
    const value = sanitized[key];

    if (typeof value === 'object' && value !== null) {
      sanitized[key] = JSON.stringify(value);
    }
  }

  return sanitized;
}
